import { isNil, isObject, isString } from 'lodash-es';

import { PrimitiveConstructable } from '@bp/shared/models/core';

import type { ICurrency } from '../currency.interface';

import { CRYPTO_CURRENCY_CODES_AND_NAMES } from './crypto-currency-codes-and-names';

export class CryptoCurrency extends PrimitiveConstructable<CryptoCurrencyCode> implements ICurrency {

	static list: CryptoCurrency[] = Object
		.entries(CRYPTO_CURRENCY_CODES_AND_NAMES)
		.map(v => <[ CryptoCurrencyCode, [ displayName: string, symbol?: string ] ]>v)
		.map(([ code, [ displayName, symbol ]]) => new CryptoCurrency({
			code,
			displayName,
			symbol: symbol ?? code,
		}));

	static fromCodes(codes: CryptoCurrencyCode[]): CryptoCurrency[] {
		return codes.map(v => new CryptoCurrency(v));
	}

	readonly code!: CryptoCurrencyCode;

	readonly displayName!: string;

	readonly symbol!: string;

	constructor(dtoOrCode: CryptoCurrencyCode | Pick<CryptoCurrency, 'code' | 'displayName' | 'symbol'>) {
		super(isString(dtoOrCode)
			? <CryptoCurrencyCode> dtoOrCode.toUpperCase()
			: dtoOrCode.code);

		if (this._isCached())
			return this;

		if (isObject(dtoOrCode))
			Object.assign(this, dtoOrCode);

		this._whenCryptoCodeCodeInvalidThrowNotFoundError();

		this._cacheAndFreezeInstance();
	}

	protected _getScope(): string {
		return 'crypto-currency';
	}

	private _whenCryptoCodeCodeInvalidThrowNotFoundError(): void {
		if (isNil(CRYPTO_CURRENCY_CODES_AND_NAMES[this.code]))
			throw new Error(`Such crypto currency doesn't exist - "${ this._id }"`);
	}
}

export type CryptoCurrencyCode = keyof typeof CRYPTO_CURRENCY_CODES_AND_NAMES;

Object.freeze(CryptoCurrency.list);
